import React, { useEffect, useState } from "react";
import { FaTrash, FaPlus, FaEdit } from "react-icons/fa";
import GroupDetail from "../GroupDetail/GroupDetail";
import CreateSelectionGroupModal from "./components/CreateSelectionGroupModal/CreateSelectionGroupModal";
import DeleteSelectionGroupModal from "./components/DeleteSelectionGroupModal/DeleteSelectionGroupModal";
import "./Table.scss";

interface SelectionGroupType {
  readonly selection_group_id: number;
  selection_group_name: string;
  selection_group_description?: string;
  local_id: number;
  items_affected?: Array<{
    item_id: number;
    item_name: string;
  }>;
  items_contained?: Array<{
    item_id: number;
    item_name: string;
  }>;
}

interface TableProps {
  data: SelectionGroupType[];
  setData: (data: SelectionGroupType[]) => void;
}

const SelectionGroupsTable: React.FC<TableProps> = ({ data, setData }) => {
  const [currentData, setCurrentData] = useState<SelectionGroupType[]>([]);
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [selectedGroup, setSelectedGroup] = useState<SelectionGroupType | null>(null);

  useEffect(() => {
    setCurrentData(data);
  }, [data]);

  const handleShowEditModal = (group: SelectionGroupType) => {
    setSelectedGroup(group);
    setShowEditModal(true);
  };

  const handleShowDeleteModal = (group: SelectionGroupType) => {
    setSelectedGroup(group);
    setShowDeleteModal(true);
  };

  const renderGroupCard = (group: SelectionGroupType) => {
    const MAX_VISIBLE = 7;
    const affected = group.items_affected || [];
    const contained = group.items_contained || [];
    const hiddenAffected = Math.max(0, affected.length - MAX_VISIBLE);
    const hiddenContained = Math.max(0, contained.length - MAX_VISIBLE);

    return (
      <div
        key={group.selection_group_id}
        className="selection-group-card menu-box like-menu"
      >
        <div className="card-top-row">
          <h2 className="group-title">{group.selection_group_name}</h2>
          <div className="card-actions">
            <button
              className="icon-btn"
              onClick={(e) => { e.stopPropagation(); handleShowEditModal(group); }}
              title="Editar"
            >
              <FaEdit />
            </button>
            <button
              className="icon-btn danger"
              onClick={(e) => { e.stopPropagation(); handleShowDeleteModal(group); }}
              title="Eliminar"
            >
              <FaTrash />
            </button>
          </div>
        </div>
        {group.selection_group_description?.trim() && (
          <p className="group-desc">{group.selection_group_description}</p>
        )}
        <div className="group-meta">
          <div className="meta-block">
            <span className="meta-label">Vinculado a</span>
            <div className="chips" title={affected.map(a=>a.item_name).join(', ')}>
              {affected.slice(0, MAX_VISIBLE).map(it => (
                <span key={it.item_id} className="chip chip-blue" data-full={it.item_name}>{it.item_name}</span>
              ))}
              {hiddenAffected > 0 && <span className="chip chip-more">+{hiddenAffected}</span>}
              {affected.length === 0 && <span className="chip chip-empty">Ninguno</span>}
            </div>
          </div>
          <div className="meta-block">
            <span className="meta-label">Productos del grupo</span>
            <div className="chips" title={contained.map(a=>a.item_name).join(', ')}>
              {contained.slice(0, MAX_VISIBLE).map(it => (
                <span key={it.item_id} className="chip chip-orange" data-full={it.item_name}>{it.item_name}</span>
              ))}
              {hiddenContained > 0 && <span className="chip chip-more">+{hiddenContained}</span>}
              {contained.length === 0 && <span className="chip chip-empty">Vacío</span>}
            </div>
          </div>
        </div>
      </div>
    );
  };

  const renderEmptyState = () => (
    <div className="empty-state">
      <div className="empty-content">
        <FaPlus className="empty-icon" />
        <h3>¡Crea tu primer grupo!</h3>
        <p>Los combos y extras te ayudan a ofrecer más opciones a tus clientes y aumentar tus ventas.</p>
        <button
          className="create-button aesthetic"
          onClick={() => setShowCreateModal(true)}
        >
          <FaPlus />
          Crear nuevo grupo
        </button>
      </div>
    </div>
  );

  if (currentData.length === 0 && !showCreateModal) {
    return (
      <div className="selection-groups-table">
        {renderEmptyState()}
        {showCreateModal && (
          <CreateSelectionGroupModal
            show={showCreateModal}
            handleClose={() => setShowCreateModal(false)}
            data={data}
            setData={setData}
          />
        )}
      </div>
    );
  }

  return (
    <div className="selection-groups-table">
      <div className="table-header">
        <button
          className="create-button"
          onClick={() => setShowCreateModal(true)}
        >
          <FaPlus />
          Crear nuevo combo
        </button>
      </div>

      <div className="groups-grid">
        {currentData.map(renderGroupCard)}
      </div>

      {/* Modals */}
      {showCreateModal && (
        <CreateSelectionGroupModal
          show={showCreateModal}
          handleClose={() => setShowCreateModal(false)}
          data={data}
          setData={setData}
        />
      )}
      
      {showEditModal && selectedGroup && (
        <GroupDetail
          show={showEditModal}
          setShow={setShowEditModal}
          sg={selectedGroup}
          data={data}
          setData={setData}
        />
      )}
      
      {showDeleteModal && selectedGroup && (
        <DeleteSelectionGroupModal
          show={showDeleteModal}
          handleClose={() => setShowDeleteModal(false)}
          sg={selectedGroup}
          data={data}
          setData={setData}
        />
      )}
    </div>
  );
};

export default SelectionGroupsTable;
